import * as turf from '@turf/turf';
import { Minimize02, Plus, Minus, Maximize02 } from '@untitled-ui/icons-react/build/cjs';
import mapboxgl from 'mapbox-gl';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import 'mapbox-gl/dist/mapbox-gl.css';
import { logError } from 'src/services/LoggingService';

import startMarkerImg from '../../../assets/img/pinWithParcel.svg';
// eslint-disable-next-line no-duplicate-imports
import endMarkerImg from '../../../assets/img/pinWithParcel.svg';

interface MapboxProps {
    startPoint: { lat: number; lng: number; name: string | null };
    endPoint?: { lat: number; lng: number; name: string | null };
    midpoint?: { lat: number; lng: number; name: string | null };
    midpoints?: {
        lat: number;
        lng: number;
        name: string | null;
        type?: string;
    }[];
    height?: any;
    className?: string;
    markerRef?: any;
    isShowRoute?: boolean;
    isZoomControlVisible?: boolean;
    isFullscreen?: boolean;
    isDraggable?: boolean;
    isZoomable?: boolean;
    defaultZoom?: any;
    setDistance?: any;
    setDuration?: any;
    showImageAsMarker?: boolean;
    currentLocation?: any;
    preventCrossBorder?: boolean; // New prop to control cross-border routing
    setIsRouteLoading?: any;
    showMapControls?: boolean; // Show custom zoom and fullscreen controls
    startMarkerColor?: string; // Color for the start point marker
    endMarkerColor?: string; // Color for the end point marker
}

const createSVGMarker = (borderColor: string) => {
    const svgElement = document.createElement('div');
    svgElement.className = 'parcel-arrow';

    // Create the marker container with inline styles for the border
    const markerContainer = document.createElement('div');
    markerContainer.style.width = '12px';
    markerContainer.style.marginTop = '-1px';
    markerContainer.style.marginRight = '-1px';
    markerContainer.style.position = 'relative';

    // Create the pseudo-element effect using a real element with inline styles
    const borderElement = document.createElement('div');
    borderElement.style.content = '';
    borderElement.style.border = `1px solid ${borderColor}`;
    borderElement.style.width = '32px';
    borderElement.style.height = '32px';
    borderElement.style.borderRadius = '50%';
    borderElement.style.position = 'absolute';
    borderElement.style.top = '50%';
    borderElement.style.left = '50%';
    borderElement.style.transform = 'translate(-50%, -50%)';
    borderElement.style.zIndex = '-1';
    borderElement.style.backgroundColor = 'transparent';

    // Create the SVG element
    const svgElement2 = document.createElement('div');
    svgElement2.innerHTML = `
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none" style="width: 100%; transform: rotate(60deg);">
            <path d="M12.6657 2.67916C12.2439 2.4686 11.7476 2.4686 11.3258 2.67916C10.9525 2.86552 10.7595 3.19367 10.6676 3.36083C10.5675 3.543 10.4649 3.77692 10.362 4.01162L3.05073 20.6813C2.93566 20.9436 2.82421 21.1976 2.7529 21.4084C2.69129 21.5905 2.56638 21.9837 2.70011 22.4066C2.84758 22.873 3.21319 23.2382 3.67975 23.3851C4.10282 23.5184 4.49584 23.393 4.67791 23.3312C4.88863 23.2596 5.14253 23.1478 5.4047 23.0324L11.9957 20.1324L18.5868 23.0325C18.8489 23.1478 19.1028 23.2596 19.3135 23.3312C19.4956 23.393 19.8886 23.5184 20.3117 23.3851C20.7783 23.2382 21.1439 22.873 21.2913 22.4066C21.4251 21.9837 21.3002 21.5905 21.2385 21.4084C21.1672 21.1976 21.0558 20.9436 20.9407 20.6813L13.6295 4.01167C13.5266 3.77695 13.424 3.54301 13.3238 3.36083C13.2319 3.19367 13.039 2.86552 12.6657 2.67916Z" fill="white"/>
          </svg>
  `;

    // Assemble the marker
    markerContainer.appendChild(borderElement);
    markerContainer.appendChild(svgElement2);
    svgElement.appendChild(markerContainer);

    return svgElement;
};

const findClosestCoord = (currentCoord: any, routeCoords: any) => {
    let minDist = Infinity;
    let closestCoord = routeCoords[0];
    routeCoords.forEach((coord: any) => {
        const dist = turf.distance(turf.point(currentCoord), turf.point(coord), {
            units: 'meters',
        });

        if (dist < minDist) {
            minDist = dist;
            closestCoord = coord;
        }
    });

    return closestCoord;
};

// Fetch driving route from Mapbox Directions API between two points
const getDrivingRouteCoordinates = async (
    start: { lat: number; lng: number },
    end: { lat: number; lng: number },
    t: any,
    preventCrossBorder: boolean = false,
    clearRoutes?: () => void,
    updatePointMarkers?: () => void
): Promise<[number, number][]> => {
    // Dynamically add exclude parameter based on preventCrossBorder prop
    const excludeParam = preventCrossBorder ? '&exclude=country_border' : '';

    const url = `https://api.mapbox.com/directions/v5/mapbox/driving/${start.lng},${start.lat};${end.lng},${end.lat}?geometries=geojson&overview=full${excludeParam}&access_token=${mapboxgl.accessToken}`;

    try {
        const res = await fetch(url);
        const data = await res.json();

        if (data.routes && data.routes.length > 0) {
            clearRoutes?.();

            updatePointMarkers?.();

            return data.routes[0].geometry.coordinates;
        }

        return [];
    } catch (error: any) {
        await logError(error, {
            errorMessage: error?.message || t('MapBox.Error_fetching_route'),
            errorLocation: 'OrderView: MapBoxMap: getDrivingRouteCoordinates',
            storeInDatabase: true,
            logToConsole: true,
        });

        return [];
    }
};

// Fetch route stats (distance & duration) separately for performance
const getRouteStats = async (start: { lat: number; lng: number }, end: { lat: number; lng: number }, t: any, preventCrossBorder: boolean = false): Promise<{ distance: number; duration: number }> => {
    // Dynamically add exclude parameter based on preventCrossBorder prop
    const excludeParam = preventCrossBorder ? '&exclude=country_border' : '';

    const url = `https://api.mapbox.com/directions/v5/mapbox/driving/${start.lng},${start.lat};${end.lng},${end.lat}?overview=false${excludeParam}&access_token=${mapboxgl.accessToken}`;

    try {
        const res = await fetch(url);
        const data = await res.json();

        if (data.routes && data.routes.length > 0) {
            return {
                distance: data.routes[0].distance,
                duration: data.routes[0].duration,
            };
        }

        return { distance: 0, duration: 0 };
    } catch (error: any) {
        await logError(error, {
            errorMessage: error?.message || t('MapBox.Error_fetching_route_stats'),
            errorLocation: 'OrderView: MapBoxMap: getRouteStats',
            storeInDatabase: true,
            logToConsole: true,
        });

        return { distance: 0, duration: 0 };
    }
};

const MapboxMap: React.FC<MapboxProps> = ({
    startPoint,
    endPoint,
    midpoint,
    midpoints,
    height = 500,
    className,
    markerRef,
    isShowRoute = false,
    isZoomControlVisible = false,
    isFullscreen = true,
    isDraggable = false,
    isZoomable = true,
    defaultZoom = 2,
    setDistance,
    setDuration,
    showImageAsMarker = false,
    currentLocation,
    preventCrossBorder = false, // Default to false to maintain backward compatibility
    setIsRouteLoading,
    showMapControls = false,
    startMarkerColor = '#444CE7', // Default blue color
    endMarkerColor = '#444CE7', // Default blue color
}) => {
    const { t } = useTranslation();
    const mapRef = useRef<any>(null);
    const mapContainerRef = useRef<any>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const [isFullscreenMode, setIsFullscreenMode] = useState(false);
    const [isMapLoaded, setIsMapLoaded] = useState(false);
    const startMarkerRef = useRef<mapboxgl.Marker | null>(null);
    const endMarkerRef = useRef<mapboxgl.Marker | null>(null);
    const currentLocationMarkerRef = useRef<mapboxgl.Marker | null>(null);

    // Handle zoom in
    const handleZoomIn = useCallback(() => {
        if (mapRef.current) {
            mapRef.current.zoomIn();
        }
    }, []);

    // Handle zoom out
    const handleZoomOut = useCallback(() => {
        if (mapRef.current) {
            mapRef.current.zoomOut();
        }
    }, []);

    // Handle fullscreen toggle
    const toggleFullscreen = useCallback(() => {
        if (!containerRef.current) return;

        if (!document.fullscreenElement) {
            containerRef.current
                .requestFullscreen()
                .then(() => {
                    setIsFullscreenMode(true);
                })
                .catch((err) => {
                    console.error('Error attempting to enable fullscreen:', err);
                });
        } else {
            document
                .exitFullscreen()
                .then(() => {
                    setIsFullscreenMode(false);
                })
                .catch((err) => {
                    console.error('Error attempting to exit fullscreen:', err);
                });
        }
    }, []);

    // Listen for fullscreen change events
    useEffect(() => {
        const handleFullscreenChange = () => {
            setIsFullscreenMode(!!document.fullscreenElement);
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);

        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
        };
    }, []);

    const updateMidpointMarkers = useCallback(() => {
        if (!mapRef.current || !isShowRoute) return;

        if (markerRef?.current) {
            if (Array.isArray(markerRef.current)) {
                markerRef.current.forEach((marker: mapboxgl.Marker) => marker?.remove());
            } else {
                markerRef.current.remove();
            }
            markerRef.current = null;
        }

        const routeSource = mapRef.current.getSource('route') as mapboxgl.GeoJSONSource;
        const routeData = routeSource?._data as any;
        const routeCoords = routeData?.geometry?.coordinates;

        if (midpoints && midpoints.length > 0) {
            const markers: mapboxgl.Marker[] = [];
            let newMidpointCoords: [number, number] | null = null;

            midpoints.forEach((mp) => {
                if (mp?.lat && mp?.lng) {
                    let markerElement;

                    if (showImageAsMarker) {
                        markerElement = document.createElement('div');
                        markerElement.style.width = '23px';
                        markerElement.style.height = '30px';
                        markerElement.style.backgroundImage = `url(${endMarkerImg})`;
                        markerElement.style.backgroundSize = 'contain';
                    } else {
                        let borderColor = '#6172F3';

                        if (mp.type === 'last') {
                            borderColor = '#F04438';
                        } else if (mp.type === 'new') {
                            borderColor = '#079455';
                        }

                        markerElement = createSVGMarker(borderColor);
                    }

                    let markerCoords: [number, number] = [mp.lng, mp.lat];

                    if (routeCoords && routeCoords.length > 0) {
                        try {
                            const snappedCoords = findClosestCoord([mp.lng, mp.lat], routeCoords);
                            markerCoords = snappedCoords as [number, number];
                        } catch (error: any) {
                            console.warn(t('MapBox.Failed_to_snap_midpoint_to_route'), error);
                        }
                    }

                    const marker = new mapboxgl.Marker(markerElement).setLngLat(markerCoords).addTo(mapRef.current);

                    markers.push(marker);

                    if (mp.type === 'new') {
                        newMidpointCoords = markerCoords;
                    }
                }
            });

            markerRef.current = markers;

            if (newMidpointCoords) {
                mapRef.current.flyTo({
                    center: newMidpointCoords,
                    zoom: 6,
                    duration: 1500,
                    essential: true,
                });
            }
        } else if (midpoint?.lat && midpoint?.lng) {
            const midpointMarker = document.createElement('div');

            if (showImageAsMarker) {
                midpointMarker.style.width = '23px';
                midpointMarker.style.height = '30px';
                midpointMarker.style.backgroundImage = `url(${endMarkerImg})`;
                midpointMarker.style.backgroundSize = 'contain';
            } else {
                midpointMarker.style.width = '10px';
                midpointMarker.style.height = '10px';
                midpointMarker.style.backgroundColor = '#444CE7';
                midpointMarker.style.borderRadius = '50%';
                midpointMarker.style.boxShadow = '0 0 5px rgba(0, 0, 0, 0.5)';
            }

            let markerCoords: [number, number] = [midpoint.lng, midpoint.lat];

            if (routeCoords && routeCoords.length > 0) {
                try {
                    const snappedCoords = findClosestCoord([midpoint.lng, midpoint.lat], routeCoords);
                    markerCoords = snappedCoords as [number, number];
                } catch (error: any) {
                    console.warn(t('MapBox.Failed_to_snap_midpoint_to_route'), error);
                }
            }

            markerRef.current = new mapboxgl.Marker(midpointMarker).setLngLat(markerCoords).addTo(mapRef.current);
        }
    }, [midpoint, midpoints, showImageAsMarker, isShowRoute, markerRef]);

    const addMarker = useCallback((map: any, position: any, imageUrl: any, name: any, showImage: any, markerColor: string = '#444CE7'): mapboxgl.Marker | null => {
        if (!position || !position.lng || !position.lat) return null;

        let markerElement;

        if (showImage) {
            markerElement = document.createElement('div');
            markerElement.style.width = '23px';
            markerElement.style.height = '30px';
            markerElement.style.backgroundImage = `url(${imageUrl})`;
            markerElement.style.backgroundSize = 'contain';
        } else {
            markerElement = document.createElement('div');
            markerElement.style.width = '10px';
            markerElement.style.height = '10px';
            markerElement.style.backgroundColor = markerColor;
            markerElement.style.borderRadius = '50%';
            markerElement.style.boxShadow = '0 0 5px rgba(0, 0, 0, 0.5)';
        }

        const marker = new mapboxgl.Marker(markerElement).setLngLat([position.lng, position.lat]).addTo(map);

        return marker;
    }, []);

    // Update start/end markers without reinitializing the map
    const updatePointMarkers = useCallback(() => {
        if (!mapRef.current || !isMapLoaded) return;

        // Remove existing start marker
        if (startMarkerRef.current) {
            startMarkerRef.current.remove();
            startMarkerRef.current = null;
        }

        // Remove existing end marker
        if (endMarkerRef.current) {
            endMarkerRef.current.remove();
            endMarkerRef.current = null;
        }

        // Add start marker
        if (startPoint && startPoint.lng) {
            startMarkerRef.current = addMarker(mapRef.current, startPoint, startMarkerImg, startPoint.name, showImageAsMarker, startMarkerColor);
        }

        // Add end marker
        if (endPoint && endPoint.lng) {
            endMarkerRef.current = addMarker(mapRef.current, endPoint, endMarkerImg, endPoint.name, showImageAsMarker, endMarkerColor);
        }
    }, [startPoint, endPoint, showImageAsMarker, addMarker, startMarkerColor, endMarkerColor, isMapLoaded, mapRef.current]);

    // Clear existing route layers and sources
    const clearRoutes = useCallback(() => {
        if (!mapRef.current) return;

        // Remove existing route layers and sources
        ['route', 'fetched-route'].forEach((id) => {
            if (mapRef.current?.getLayer(id)) {
                mapRef.current.removeLayer(id);
            }

            if (mapRef.current?.getSource(id)) {
                mapRef.current.removeSource(id);
            }
        });
    }, []);

    // Update route and markers on map
    const updateRouteAndMarkers = useCallback(async () => {
        if (!mapRef.current || !isShowRoute || !startPoint?.lng) return;

        let allCoordinates: [number, number][] = [];

        try {
            if (midpoints && midpoints.length > 0 && endPoint?.lat && endPoint?.lng) {
                const validMidpoints = midpoints.filter((mp) => mp?.lat && mp?.lng);

                if (validMidpoints.length > 0) {
                    const routeSegments = [];
                    let currentPoint = startPoint;

                    for (const waypoint of validMidpoints) {
                        const segment = await getDrivingRouteCoordinates(currentPoint, waypoint, t, preventCrossBorder, clearRoutes, updatePointMarkers);
                        routeSegments.push(segment);
                        currentPoint = waypoint;
                    }

                    const finalSegment = await getDrivingRouteCoordinates(currentPoint, endPoint, t, preventCrossBorder, clearRoutes, updatePointMarkers);
                    routeSegments.push(finalSegment);

                    allCoordinates = routeSegments.flat();
                }
            } else if (midpoint?.lat && midpoint?.lng && endPoint?.lat && endPoint?.lng) {
                const [route1, route2] = await Promise.all([
                    getDrivingRouteCoordinates(startPoint, midpoint, t, preventCrossBorder, clearRoutes, updatePointMarkers),
                    getDrivingRouteCoordinates(midpoint, endPoint, t, preventCrossBorder, clearRoutes, updatePointMarkers),
                ]);

                allCoordinates = [...route1, ...route2];
            } else if (endPoint?.lat && endPoint?.lng) {
                // Direct route: start → end
                const route = await getDrivingRouteCoordinates(startPoint, endPoint, t, preventCrossBorder, clearRoutes, updatePointMarkers);
                allCoordinates = route;
            }

            if (allCoordinates.length > 0) {
                // Add route source and layer
                mapRef.current.addSource('route', {
                    type: 'geojson',
                    data: {
                        type: 'Feature',
                        properties: {},
                        geometry: {
                            type: 'LineString',
                            coordinates: allCoordinates,
                        },
                    },
                });

                mapRef.current.addLayer({
                    id: 'route',
                    type: 'line',
                    source: 'route',
                    layout: {
                        'line-join': 'round',
                        'line-cap': 'round',
                    },
                    paint: {
                        'line-color': '#444CE7',
                        'line-width': 3,
                        'line-opacity': 0.75,
                    },
                });

                // Fit bounds to show entire route
                const bounds = new mapboxgl.LngLatBounds();
                allCoordinates.forEach((coord) => bounds.extend(coord));

                mapRef.current.fitBounds(bounds, {
                    padding: { top: 50, bottom: 50, left: 50, right: 50 },
                    maxZoom: 10,
                });

                setTimeout(() => {
                    updateMidpointMarkers();
                }, 50);

                // Calculate distance and duration after route is rendered (non-blocking)
                if (setDistance || setDuration) {
                    setTimeout(async () => {
                        try {
                            let totalDistance = 0;
                            let totalDuration = 0;

                            if (midpoints && midpoints.length > 0 && endPoint?.lat && endPoint?.lng) {
                                const validMidpoints = midpoints.filter((mp) => mp?.lat && mp?.lng);

                                if (validMidpoints.length > 0) {
                                    let currentPoint = startPoint;

                                    for (const waypoint of validMidpoints) {
                                        const stats = await getRouteStats(currentPoint, waypoint, t, preventCrossBorder);
                                        totalDistance += stats.distance;
                                        totalDuration += stats.duration;
                                        currentPoint = waypoint;
                                    }

                                    const finalStats = await getRouteStats(currentPoint, endPoint, t, preventCrossBorder);
                                    totalDistance += finalStats.distance;
                                    totalDuration += finalStats.duration;
                                }
                            } else if (midpoint?.lat && midpoint?.lng && endPoint?.lat && endPoint?.lng) {
                                const [stats1, stats2] = await Promise.all([getRouteStats(startPoint, midpoint, t, preventCrossBorder), getRouteStats(midpoint, endPoint, t, preventCrossBorder)]);
                                totalDistance = stats1.distance + stats2.distance;
                                totalDuration = stats1.duration + stats2.duration;
                            } else if (endPoint?.lat && endPoint?.lng) {
                                const stats = await getRouteStats(startPoint, endPoint, t, preventCrossBorder);
                                totalDistance = stats.distance;
                                totalDuration = stats.duration;
                            }

                            if (setDistance) {
                                const routeDistance = Math.round((totalDistance / 1000) * 0.621371); // Convert to miles
                                setDistance(routeDistance);
                            }

                            if (setDuration) {
                                const hours = Math.floor(totalDuration / 3600);
                                const minutes = Math.floor((totalDuration % 3600) / 60);
                                const formattedDuration = `${hours} hr ${minutes} min`;
                                setDuration(formattedDuration);
                            }

                            if (setIsRouteLoading) {
                                setIsRouteLoading(false);
                            }
                        } catch (error: any) {
                            await logError(error, {
                                errorMessage: error?.message || t('MapBox.Error_calculating_route_stats'),
                                errorLocation: 'OrderView: MapBoxMap: updateRouteAndMarkers',
                                storeInDatabase: true,
                                logToConsole: true,
                            });

                            if (setIsRouteLoading) {
                                setIsRouteLoading(false);
                            }
                        }
                    }, 100); // Small delay to ensure route renders first
                } else {
                    // No route found, reset loading state
                    if (setIsRouteLoading) {
                        setIsRouteLoading(false);
                    }
                }
            }
        } catch (error: any) {
            await logError(error, {
                errorMessage: error?.message || t('MapBox.Error_updating_route'),
                errorLocation: 'OrderView: MapBoxMap: updateRouteAndMarkers',
                storeInDatabase: true,
                logToConsole: true,
            });

            if (setIsRouteLoading) {
                setIsRouteLoading(false);
            }
        }
    }, [startPoint, endPoint, midpoint, midpoints, isShowRoute, clearRoutes, preventCrossBorder]);

    const initializeMap = useCallback(() => {
        if (!window?.MAPBOX_ACCESSTOKEN) {
            console.error(t('MapBox.Mapbox_access_token_missing'));

            return;
        }

        mapboxgl.accessToken = window.MAPBOX_ACCESSTOKEN;

        mapRef.current = new mapboxgl.Map({
            container: mapContainerRef.current,
            style: 'mapbox://styles/mapbox/light-v11',
            center: [-95, 55],
            zoom: defaultZoom,
            pitch: 0,
            bearing: 0,
            boxZoom: isZoomable,
            antialias: true,
            dragPan: isDraggable,
            scrollZoom: isZoomable,
            doubleClickZoom: isZoomable,
            touchZoomRotate: isZoomable,
            projection: 'mercator',
            attributionControl: false,
            dragRotate: false,
        });

        updatePointMarkers();

        if (isZoomControlVisible) {
            mapRef.current.addControl(new mapboxgl.NavigationControl());
        }

        // Fullscreen control
        if (isFullscreen) {
            const fullscreenControl = new mapboxgl.FullscreenControl();
            mapRef.current.addControl(fullscreenControl);
        }

        mapRef.current.on('load', async () => {
            setIsMapLoaded(true);
        });
    }, [defaultZoom, isZoomable, isDraggable, isZoomControlVisible, isFullscreen, t, isFullscreenMode]);

    useEffect(() => {
        initializeMap();

        return () => {
            setIsMapLoaded(false);

            if (startMarkerRef.current) {
                startMarkerRef.current.remove();
                startMarkerRef.current = null;
            }

            if (endMarkerRef.current) {
                endMarkerRef.current.remove();
                endMarkerRef.current = null;
            }

            if (currentLocationMarkerRef.current) {
                currentLocationMarkerRef.current.remove();
                currentLocationMarkerRef.current = null;
            }

            if (mapRef.current) {
                mapRef.current.remove();
                mapRef.current = null;
            }
        };
    }, [initializeMap]);

    // Update current location marker
    useEffect(() => {
        if (!mapRef.current || !isMapLoaded) return;

        // Remove existing current location marker
        if (currentLocationMarkerRef.current) {
            currentLocationMarkerRef.current.remove();
            currentLocationMarkerRef.current = null;
        }

        // Add current location marker if provided
        if (currentLocation) {
            const svgMarker = createSVGMarker('#6172F3');
            currentLocationMarkerRef.current = new mapboxgl.Marker(svgMarker).setLngLat([currentLocation.lng, currentLocation.lat]).addTo(mapRef.current!);
        }
    }, [currentLocation, isMapLoaded]);

    // Update routes and fit bounds when points change
    useEffect(() => {
        if (!mapRef.current || !isMapLoaded) return;

        const updateMapView = async () => {
            await updateRouteAndMarkers();
            updatePointMarkers();

            // Set view based on points
            if (!isShowRoute) {
                const bounds = new mapboxgl.LngLatBounds();

                if (startPoint && startPoint.lng) {
                    bounds.extend([startPoint.lng, startPoint.lat]);
                }

                if (endPoint && endPoint.lng) {
                    bounds.extend([endPoint.lng, endPoint.lat]);
                }

                if (startPoint && endPoint) {
                    mapRef?.current?.fitBounds(bounds, {
                        padding: { top: 50, bottom: 50, left: 50, right: 50 },
                        maxZoom: 10,
                    });
                } else if (startPoint) {
                    mapRef?.current?.setCenter([startPoint.lng, startPoint.lat]);
                    mapRef?.current?.setZoom(defaultZoom > 16 ? defaultZoom : 16);
                } else if (endPoint) {
                    mapRef?.current?.setCenter([endPoint.lng, endPoint.lat]);
                    mapRef?.current?.setZoom(defaultZoom > 16 ? defaultZoom : 16);
                }
            }
        };

        updateMapView();
    }, [isMapLoaded, startPoint, endPoint, midpoints, isShowRoute, updateRouteAndMarkers, defaultZoom]);

    useEffect(
        () => () => {
            if (markerRef?.current) {
                if (Array.isArray(markerRef.current)) {
                    markerRef.current.forEach((marker: mapboxgl.Marker) => marker?.remove());
                } else {
                    markerRef.current.remove();
                }
                markerRef.current = null;
            }
        },
        []
    );

    return (
        <div
            ref={containerRef}
            style={{
                position: 'relative',
                width: '100%',
                height: isFullscreenMode ? '100vh' : typeof height === 'string' ? height : `${height}px`,
            }}
        >
            <div id="map-container" ref={mapContainerRef} style={{ width: '100%', height: '100%' }} className={className} />

            {/* Custom Map Controls */}
            {showMapControls && (
                <div className="absolute top-2 right-2 flex flex-col gap-2">
                    {/* Zoom Controls */}
                    <div className="bg-white rounded-lg shadow-md border border-utilityGray200 overflow-hidden">
                        <button
                            onClick={handleZoomIn}
                            className="w-9 h-9 flex items-center justify-center hover:bg-gray50 transition-colors border-b border-utilityGray200"
                            title="Zoom In"
                            type="button"
                        >
                            <Plus className="w-5 h-5 text-gray500" />
                        </button>
                        <button onClick={handleZoomOut} className="w-9 h-9 flex items-center justify-center hover:bg-gray50 transition-colors" title="Zoom Out" type="button">
                            <Minus className="w-5 h-5 text-gray500" />
                        </button>
                    </div>

                    {/* Fullscreen Control */}
                    <div className="bg-white rounded-lg shadow-md border border-utilityGray200 overflow-hidden">
                        <button
                            onClick={toggleFullscreen}
                            className="w-9 h-9 flex items-center justify-center hover:bg-gray50 transition-colors"
                            title={isFullscreenMode ? t('Common.Exit_Fullscreen') : t('Common.Enter_Fullscreen')}
                            type="button"
                        >
                            {isFullscreenMode ? <Minimize02 className="w-5 h-5 text-gray500" /> : <Maximize02 className="w-5 h-5 text-gray500" />}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MapboxMap;
